import { XMarkIcon } from "@heroicons/react/24/outline";
import { useUtils } from "@/app/hooks/useUtils";
import { useCallback, useEffect, useState } from "react";
import { CiFlag1 } from "react-icons/ci";
import { ExemptionRow } from "@/api_framework/api_modals/Cart";
import { useDeleteBookingExemptionAPI } from "@/app/hooks/api_hooks/Cart/useCartExemptionsDeleteApi";
import { useExemptionEligibleAPI } from "@/app/hooks/api_hooks/Cart/useCartExemptionsAPI";
import ExamptionModalComponent from "@/app/components/routes/auth_routes/ClinicClosingComponent/ExamptionModalComponent";
import TableSkeletonComponent from "../../LoaderComponent";

interface Props {
  open: boolean;
  onClose: () => void;
  customerId?: string | null;
  clinicId?: string | null;
  customerName?: string;
  onRefresh?: () => void;
}

const RaiseExemptionModel = ({
  open,
  onClose,
  customerId,
  clinicId,
  customerName,
  onRefresh,
}: Props) => {
  const { veticDateFormat } = useUtils();
  const { execute: deleteExemption } = useDeleteBookingExemptionAPI();
  const { execute: fetchExemptions, data: exemptionData } = useExemptionEligibleAPI();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ExemptionRow | null>(null);
  const [actionType, setActionType] = useState<"Remove Exemption" | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [openCreateExemption, setOpenCreateExemption] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<ExemptionRow | null>(null);

  const rows: ExemptionRow[] = Array.isArray(exemptionData) ? exemptionData : [];

  const loadExemptions = useCallback(async () => {
    if (!customerId || !clinicId) return;
    setIsLoading(true);
    await fetchExemptions(customerId, clinicId);
    setIsLoading(false);
    setHasLoadedOnce(true);
  }, [customerId, clinicId, fetchExemptions]);

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    loadExemptions();
  }, [open, loadExemptions]);

  useEffect(() => {
    if (!open) {
      setOpenCreateExemption(false);
      setSelectedBooking(null);
    }
  }, [open]);

  if (!open) return null;

  const handleRaiseClick = (row: ExemptionRow) => {
    setSelectedBooking(row);
    setOpenCreateExemption(true);
  };

  const handleCancelClick = (row: ExemptionRow) => {
    setSelectedRow(row);
    setActionType("Remove Exemption");
    setConfirmOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedRow) return;

    setIsDeleting(true);

    const success = await deleteExemption(
      selectedRow.booking_id,
      selectedRow.exemption_details?.l1 ?? "" 
    );

    setIsDeleting(false);

    if (success) {
      setConfirmOpen(false);
      setSelectedRow(null);
      setActionType(null);
      await loadExemptions();
      onRefresh?.();
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white w-[95%] max-w-6xl rounded-xl shadow-xl p-5 max-h-[85vh] overflow-y-auto">

          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium">
              {customerName ? `${customerName}'s Appointments` : "Appointments"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-black text-xl"
            >
              ✕
            </button>
          </div>

          {/* Table */}
          {isLoading || !hasLoadedOnce ? (
            <TableSkeletonComponent
              columnLength={7}
              rowLength={1}
              containerClassName="mt-0 h-auto overflow-x-auto border rounded-xl"
            />
          ) : rows?.length > 0 ? (
            <div className="overflow-x-auto border rounded-xl">
              <table className="w-full table-auto">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="px-3 py-3 text-xs text-left font-normal whitespace-nowrap">SR.NO</th>
                    <th className="px-4 py-3 text-xs text-left font-normal whitespace-nowrap">Pet Name</th>
                    <th className="px-4 py-3 text-xs text-left font-normal whitespace-nowrap">Service Type</th>
                    <th className="px-4 py-3 text-xs text-left font-normal whitespace-nowrap">Start Time</th>
                    <th className="px-4 py-3 text-xs text-left font-normal whitespace-nowrap">Provider</th>
                    <th className="px-4 py-3 text-xs text-left font-normal whitespace-nowrap">Exemption</th>
                    <th className="px-4 py-3 text-xs text-left font-normal whitespace-nowrap">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.booking_id} className="border-t">

                      <td className="px-3 py-3 text-xs whitespace-nowrap">{index + 1}</td>

                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <span className="font-medium ">{row?.pet_details?.name}</span>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        <div className="flex flex-col">
                          <span>{row?.service_details?.service_name}</span>
                          <span className="text-gray-500">{row?.service_details?.sub_service_name}</span>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                        {veticDateFormat(row?.booking_details?.start_datetime)}
                      </td>

                      <td className="px-4 py-3 text-xs whitespace-normal break-words max-w-[140px]">
                        {row?.staff_details?.name || "NA"}
                      </td>

                      <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                        {row?.exemption_details ? (
                          <div>{row.exemption_details.l1}</div>
                        ) : (
                          <span className="text-gray-400">No Exemption</span>
                        )}
                      </td>

                      <td className=" py-3 text-xs whitespace-nowrap">
                        <div className="flex gap-4">

                          {!row?.exemption_details && (
                            <button
                              type="button"
                              onClick={() => handleRaiseClick(row)}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-black text-white text-xs font-medium"
                            >
                              <CiFlag1 className="h-4 w-4" />
                              Mark as Exemption
                            </button>
                          )}

                          {row?.exemption_details && (
                            <button
                              type="button"
                              onClick={() => handleCancelClick(row)}
                              className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-white text-red-600 border border-red-600 text-xs font-medium"
                            >
                              <XMarkIcon className="h-4 w-4" />
                              Remove Exemption
                            </button>
                          )}

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10 text-sm text-gray-500">
              No Exemption Found
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmOpen && actionType === "Remove Exemption" && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[500px] rounded-xl shadow-xl p-6">
            <h3 className="text-lg font-semibold mb-4">Confirmation</h3>

            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to{" "}
              <span className="font-semibold capitalize">{actionType}</span>?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setConfirmOpen(false);
                  setSelectedRow(null);
                  setActionType(null);
                }}
                disabled={isDeleting}
                className="px-4 py-2 border rounded-lg text-sm disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                onClick={handleConfirmAction}
                disabled={isDeleting}
                className="px-4 py-2 rounded-lg text-white text-sm bg-red-600 disabled:opacity-50 flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Removing...
                  </>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ExamptionModalComponent
        open={openCreateExemption && !!selectedBooking}
        setOpen={(val) => {
          if (!val) {
            setSelectedBooking(null);
          }
          setOpenCreateExemption(val);
        }}
        bookingDetails={selectedBooking}
        fetchAppointmentData={() => {
          loadExemptions();
          onRefresh?.();
        }}
      />
    </>
  );
};

export default RaiseExemptionModel;
